<template>
    <modal :open="show" size="screen" padding="px-0" @close="$emit('closeModal')">
        <div class="flex flex-col max-h-[85vh]">
            <div class="flex items-center justify-between px-6 pb-3 border-b border-black/10 dark:border-gray-600">
                <div>
                    <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
                        Webhooks Nexus
                    </h2>
                    <p class="text-xs text-gray-600 dark:text-gray-300">
                        Transação #{{ transaction.id }} — Hash {{ transaction.hash }}
                        <span v-if="client"> — Parceiro {{ client.name }}</span>
                    </p>
                </div>
                <div class="flex items-center gap-3">
                    <button
                        v-if="canResend && webhooks.length > 0"
                        type="button"
                        class="btn-primary"
                        :disabled="resending || loading"
                        @click="resendWebhook"
                    >
                        {{ resendLabel }}
                    </button>
                    <span
                        v-else-if="reason && webhooks.length > 0"
                        class="text-xs text-yellow-400"
                    >
                        {{ reason }}
                    </span>
                    <button
                        type="button"
                        class="btn-outline-primary px-5 py-2"
                        :disabled="loading"
                        @click="fetch"
                    >
                        Atualizar
                    </button>
                    <button
                        class="text-gray-600 dark:text-gray-300 hover:text-white"
                        @click="$emit('closeModal')"
                    >
                        <XMarkIcon class="h-6 w-6" />
                    </button>
                </div>
            </div>

            <div class="flex-1 overflow-y-auto px-6 py-4">
                <ticto-loading v-if="loading" />

                <div
                    v-else-if="webhooks.length === 0"
                    class="flex flex-col items-center gap-3 py-10 text-center text-gray-600 dark:text-gray-300"
                >
                    <p>Nenhum webhook desta cobrança foi registrado para o parceiro Nexus.</p>
                    <button
                        v-if="canResend"
                        type="button"
                        class="btn-primary"
                        :disabled="resending"
                        @click="resendWebhook"
                    >
                        {{ resendLabel }}
                    </button>
                    <p v-else-if="reason" class="text-xs text-yellow-400">{{ reason }}</p>
                </div>

                <ol v-else class="relative border-l border-black/10 dark:border-gray-600 ml-3">
                    <li
                        v-for="webhook in webhooks"
                        :key="webhook.id"
                        class="mb-6 ml-6"
                    >
                        <span
                            class="absolute -left-3 flex h-6 w-6 items-center justify-center rounded-full ring-4 ring-gray-700"
                            :class="indicatorBg(webhook)"
                        >
                            <XMarkIcon v-if="webhook.status === 'failed'" class="h-3 w-3 text-gray-900 dark:text-white" />
                            <ClockIcon v-else-if="webhook.status === 'pending'" class="h-3 w-3 text-gray-900 dark:text-white" />
                            <CheckIcon v-else class="h-3 w-3 text-gray-900 dark:text-white" />
                        </span>

                        <div class="rounded-lg bg-gray-800 p-3">
                            <div class="flex flex-wrap items-center gap-2">
                                <span class="rounded bg-indigo-600 px-2 py-0.5 text-xs font-medium text-white">
                                    {{ webhook.event }}
                                </span>
                                <span
                                    class="rounded px-2 py-0.5 text-xs font-medium text-gray-900 dark:text-white"
                                    :class="indicatorBg(webhook)"
                                >
                                    {{ statusLabel(webhook.status) }}
                                </span>
                                <span
                                    v-if="webhook.replay"
                                    class="rounded bg-yellow-600 px-2 py-0.5 text-xs font-medium text-white"
                                >
                                    Reenvio manual
                                </span>
                                <span class="ml-auto flex items-center text-xs text-gray-600 dark:text-gray-300">
                                    <ClockIcon class="mr-1 h-4 w-4" />
                                    {{ formatDate(webhook.created_at) }}
                                </span>
                            </div>

                            <div class="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-100">
                                <div class="md:col-span-2 break-all">
                                    <span class="text-gray-400">Endpoint:</span>
                                    {{ webhook.endpoint || '—' }}
                                </div>
                                <div>
                                    <span class="text-gray-400">Origem:</span>
                                    {{ webhook.origin || '—' }}
                                </div>
                                <div>
                                    <span class="text-gray-400">Status no payload:</span>
                                    {{ webhook.payload?.data?.status || '—' }}
                                </div>
                                <div>
                                    <span class="text-gray-400">ID:</span>
                                    #{{ webhook.id }}
                                </div>
                                <div>
                                    <span class="text-gray-400">Atualizado em:</span>
                                    {{ formatDate(webhook.updated_at) }}
                                </div>
                            </div>

                            <div class="mt-2 text-xs text-gray-100">
                                <span class="text-gray-400">Tentativas de entrega:</span>
                                <span v-if="webhook.deliveries.length === 0"> nenhuma registrada</span>
                                <ul v-else class="mt-1 space-y-1">
                                    <li
                                        v-for="delivery in webhook.deliveries"
                                        :key="delivery.id"
                                        class="flex flex-wrap items-center gap-2"
                                    >
                                        <span
                                            class="rounded px-2 py-0.5 text-xs font-medium text-white"
                                            :class="deliveryBg(delivery)"
                                        >
                                            HTTP {{ delivery.http_status || '—' }}
                                        </span>
                                        <span>{{ formatDate(delivery.sent_at || delivery.created_at) }}</span>
                                        <span v-if="delivery.error_message" class="text-red-300">
                                            {{ delivery.error_message }}
                                        </span>
                                    </li>
                                </ul>
                            </div>

                            <button
                                type="button"
                                class="btn-primary mt-2"
                                @click="toggle(webhook.id)"
                            >
                                {{ expanded === webhook.id ? 'Ocultar payload' : 'Ver payload' }}
                            </button>

                            <div v-if="expanded === webhook.id" class="mt-3">
                                <pre
                                    class="max-h-72 overflow-auto rounded bg-gray-900 p-3 text-xs text-white whitespace-pre-wrap break-all"
                                >{{ formatPayload(webhook.payload) }}</pre>
                            </div>
                        </div>
                    </li>
                </ol>
            </div>
        </div>
    </modal>
</template>

<script>
import { CheckIcon, ClockIcon, XMarkIcon } from "@heroicons/vue/24/outline";
import moment from "moment";
import Modal from "../../../components/layouts/Modal.vue";
import TictoLoading from "../../../components/layouts/TictoLoading.vue";

/**
 * TIC2-16002: webhooks de cobrança registrados pelo Kraken para o parceiro Nexus
 * (tabela nexus_webhooks), com reenvio do transaction_updated pela equipe Ticto.
 */
export default {
    components: { Modal, TictoLoading, CheckIcon, ClockIcon, XMarkIcon },
    props: {
        transaction: { type: Object, required: true },
        show: { type: Boolean, default: false },
    },
    emits: ["closeModal"],
    data() {
        return {
            loading: false,
            resending: false,
            webhooks: [],
            client: null,
            canResend: false,
            reason: null,
            nextEvent: null,
            expanded: null,
        };
    },
    computed: {
        isFirstSend() {
            return this.webhooks.length === 0;
        },
        resendLabel() {
            if (this.resending) {
                return "Enviando...";
            }
            return this.isFirstSend
                ? `Enviar webhook de criação (${this.nextEvent || "transaction_created"})`
                : `Reenviar webhook (${this.nextEvent || "transaction_updated"})`;
        },
    },
    watch: {
        show(value) {
            if (value) {
                this.fetch();
            }
        },
    },
    mounted() {
        if (this.show) {
            this.fetch();
        }
    },
    methods: {
        statusLabel(status) {
            return (
                { pending: "Pendente", sent: "Enviado", failed: "Falhou" }[status] ||
                status ||
                "—"
            );
        },
        indicatorBg(webhook) {
            if (webhook.status === "failed") {
                return "bg-red-500";
            }
            if (webhook.status === "pending") {
                return "bg-yellow-500";
            }
            return "bg-green-500";
        },
        deliveryBg(delivery) {
            if (delivery.http_status >= 200 && delivery.http_status < 300) {
                return "bg-green-600";
            }
            return delivery.http_status ? "bg-red-600" : "bg-gray-600";
        },
        formatDate(value) {
            return value ? moment(value).format("DD/MM/YYYY HH:mm:ss") : "—";
        },
        formatPayload(payload) {
            if (!payload) {
                return "—";
            }
            return typeof payload === "object"
                ? JSON.stringify(payload, null, 2)
                : payload;
        },
        toggle(id) {
            this.expanded = this.expanded === id ? null : id;
        },
        fetch() {
            this.loading = true;

            axios
                .get(
                    `/api/nexus/partners/transactions/${this.transaction.hash}/webhooks`,
                )
                .then(({ data }) => {
                    this.webhooks = data.webhooks || [];
                    this.client = data.client;
                    this.canResend = !!data.can_resend;
                    this.reason = data.reason;
                    this.nextEvent = data.next_event;
                })
                .catch(({ response }) => {
                    this.$notify(
                        {
                            group: "error",
                            title: "Erro",
                            text:
                                response?.data?.message ||
                                "Falha ao carregar webhooks Nexus.",
                        },
                        4000,
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        resendWebhook() {
            const message = this.isFirstSend
                ? `Nenhum webhook desta cobrança foi registrado para o parceiro Nexus. Enviar o webhook de criação (${this.nextEvent} com o status atual: ${this.transaction.status}) da cobrança ${this.transaction.hash}?`
                : `Reenviar o webhook (${this.nextEvent} com o status atual: ${this.transaction.status}) da cobrança ${this.transaction.hash} ao parceiro Nexus?`;
            if (!window.confirm(message)) {
                return;
            }

            this.resending = true;

            axios
                .post(
                    `/api/nexus/partners/transactions/${this.transaction.hash}/resend-webhook`,
                )
                .then(({ data }) => {
                    this.$notify(
                        {
                            group: "success",
                            title: "Webhook",
                            text: data.message || "Envio do webhook solicitado.",
                        },
                        4000,
                    );
                    // O Kraken registra o webhook ao receber o job; dá um respiro antes de recarregar.
                    setTimeout(() => this.fetch(), 1500);
                })
                .catch(({ response }) => {
                    this.$notify(
                        {
                            group: "error",
                            title: "Erro",
                            text:
                                response?.status === 429
                                    ? "Limite atingido: aguarde um minuto para reenviar esta cobrança de novo."
                                    : response?.data?.message ||
                                      "Erro ao reenviar webhook.",
                        },
                        4000,
                    );
                })
                .finally(() => {
                    this.resending = false;
                });
        },
    },
};
</script>
